import { FC, useState } from 'react';
import { FiMapPin, FiBriefcase, FiDollarSign, FiEdit } from 'react-icons/fi';
import { Lead } from 'models/Lead';
import { Link } from 'react-router-dom';
import { Button, ExpandableText } from 'components';
import classNames from 'classnames';
import { leadStatusOptions } from 'constants/leadStatusOptions';
import { EditCompanyNameModal } from './components';

import './LeadCard.styles.scss';

type LeadCardProps = {
  lead: Lead;
  hasLink?: boolean;
  hasExpandableJobDesc?: boolean;
  className?: string;
  onQuickPreview?: (lead: Lead) => void;
};

const LeadCard: FC<LeadCardProps> = (props) => {
  const {
    lead,
    hasLink = true,
    hasExpandableJobDesc = false,
    className,
    onQuickPreview,
  } = props;

  const isDeclined = lead.isArchived;

  const leadStatus = isDeclined
    ? 'declined'
    : leadStatusOptions()
        .find((option) => option.value === lead?.contact?.leadStatus)
        ?.label?.toLocaleLowerCase() || 'no lead';

  const link = lead.listingUrl || lead.url;
  const [editCompanyNameModalOpen, setEditCompanyNameModalOpen] =
    useState(false);

  return (
    <div className={classNames('lead-card', className)}>
      <div className="lead-card__top">
        <div>
          <span className="company-name">
            Company: <span className="primary-color">{lead.companyName}</span>
            <Button
              variant="icon"
              className="lead-card__edit-btn"
              onClick={() => setEditCompanyNameModalOpen(true)}
            >
              <FiEdit />
              <EditCompanyNameModal
                isOpen={editCompanyNameModalOpen}
                onClose={() => setEditCompanyNameModalOpen(false)}
                lead={lead}
              />
            </Button>
          </span>
          {lead?.campaign && (
            <span className="company-name">
              Campaign:{' '}
              <Link
                to={`/campaigns/${lead.campaign.id}`}
                className="lead-card__link"
              >
                {lead?.campaign?.name}
              </Link>
            </span>
          )}
          {!hasLink && (
            <span className="company-name">
              Created at: {new Date(lead.createdAt).toLocaleDateString()}
            </span>
          )}

          <span
            className={`status status--${leadStatus
              .toLowerCase()
              .split(' ')
              .join('-')}`}
          >
            {leadStatus}
          </span>
        </div>
        <h3 className="job-title">{lead.jobTitle}</h3>
        {link ? (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="listing-link subtle"
          >
            See original job posting ↗
          </a>
        ) : (
          <span className="link-placeholder">placeholder</span>
        )}
      </div>

      <div className="lead-details">
        <div className="detail">
          <FiMapPin className="icon" />
          <span>Location: {lead.location || 'Not Found'}</span>
        </div>

        <div className="detail">
          <FiBriefcase className="icon" />
          <span>Job type: {lead.jobType || 'Not Found'}</span>
        </div>

        <div className="detail">
          <FiDollarSign className="icon" />
          <span>Salary: {lead.salary || 'Not Found'}</span>
        </div>
      </div>

      {hasExpandableJobDesc ? (
        <ExpandableText text={lead.fullJobDescription} />
      ) : (
        <p
          className={'job-description'}
          dangerouslySetInnerHTML={{ __html: lead.fullJobDescription }}
        />
      )}
      <div className="lead-card__footer">
        {hasLink && (
          <span>
            Created at: {new Date(lead.createdAt).toLocaleDateString()}
          </span>
        )}
        {hasLink && (
          <div className="lead-card__actions">
            <Button
              className="lead-card__preview-btn"
              onClick={() => onQuickPreview?.(lead)}
            >
              Quick preview
            </Button>
            <Link to={`/lead/${lead._id}`} className="lead-card__link">
              Go to full page →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadCard;
